"use client";

import { useCallback, useEffect, useState } from "react";
import { AdminKeyGate } from "@/components/admin-key-gate";
import { Card, Spinner } from "@/components/ui";
import { AdminAuthError, adminFetch } from "@/lib/admin-client";

interface AuditLog {
  id: number;
  actorId: string;
  action: string;
  targetType: string;
  targetId?: string;
  ip: string;
  details?: Record<string, unknown>;
  createdAt: string;
}

export default function AdminAuditPage() {
  const [logs, setLogs] = useState<AuditLog[] | null>(null);
  const [needKey, setNeedKey] = useState(false);
  const load = useCallback(async () => {
    try {
      const result = await adminFetch<{ logs: AuditLog[] }>("/api/admin/audit");
      setLogs(result.logs);
      setNeedKey(false);
    } catch (error) {
      if (error instanceof AdminAuthError) setNeedKey(true);
    }
  }, []);
  useEffect(() => void load(), [load]);

  if (needKey) return <AdminKeyGate onUnlock={() => void load()} />;
  if (!logs) {
    return <div className="grid min-h-[60vh] place-items-center"><Spinner /></div>;
  }
  return (
    <div className="mx-auto max-w-6xl px-5 py-10">
      <h1 className="text-2xl font-semibold tracking-tight">Admin audit</h1>
      <p className="mt-2 text-sm text-muted">Latest 200 administrative changes. Entries are append-only.</p>
      <Card className="mt-6 overflow-x-auto p-0">
        <table className="w-full min-w-[820px] text-left text-xs">
          <thead className="border-b border-line text-muted">
            <tr>{["Time", "Actor", "Action", "Target", "IP", "Details"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.id} className="border-b border-line/60 last:border-0">
                <td className="whitespace-nowrap px-4 py-3 text-muted">{new Date(log.createdAt).toLocaleString()}</td>
                <td className="max-w-40 truncate px-4 py-3">{log.actorId}</td>
                <td className="px-4 py-3 font-medium">{log.action}</td>
                <td className="px-4 py-3">{log.targetType}{log.targetId ? ` · ${log.targetId}` : ""}</td>
                <td className="px-4 py-3 text-muted">{log.ip}</td>
                <td className="max-w-64 truncate px-4 py-3 text-muted">{log.details ? JSON.stringify(log.details) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!logs.length && <div className="p-8 text-center text-sm text-muted">No admin changes recorded yet.</div>}
      </Card>
    </div>
  );
}

